import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import {
  getAuth,
  signOut,
  onAuthStateChanged,
  updateProfile,
  RecaptchaVerifier,
  signInWithPhoneNumber
} from "firebase/auth";
import app, { db } from '../firebase';
import {
  collection,
  doc,
  onSnapshot,
  setDoc,
  deleteDoc,
  updateDoc,
  getDocs,
  writeBatch,
  getDoc
} from "firebase/firestore";

const CartContext = createContext();

export const useCart = () => {
  return useContext(CartContext);
};

const getLocalCart = () => {
  try {
    const localData = localStorage.getItem('shoppingCart');
    return localData ? JSON.parse(localData) : [];
  } catch (error) {
    console.error("Could not parse local storage cart", error);
    return [];
  }
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [isSizeChartOpen, setIsSizeChartOpen] = useState(false);

  const [currentUser, setCurrentUser] = useState(null);
  const [authError, setAuthError] = useState(null);
  const [authMessage] = useState(null);

  const [confirmationResult, setConfirmationResult] = useState(null);
  
  const auth = getAuth(app);
  const cartListenerRef = useRef(null); 

  const openSizeChart = () => setIsSizeChartOpen(true);
  const closeSizeChart = () => setIsSizeChartOpen(false);

  // --- UPDATED: Safely handles items without a variants array (Accessories) ---
  const fetchFreshCartData = async (rawCartItems) => {
    if (!rawCartItems || rawCartItems.length === 0) return [];
    
    const freshItems = await Promise.all(
      rawCartItems.map(async (item) => {
        try {
          const docId = item.productId || item.id; 
          const productRef = doc(db, 'Products', docId);
          const productSnap = await getDoc(productRef);
          
          if (productSnap.exists()) {
            const liveData = productSnap.data();
            
            // If the item has a variants array (Clothing)
            if (liveData.variants && liveData.variants.length > 0) {
              let currentVariant = liveData.variants[0]; 
              if (item.variantId) {
                currentVariant = liveData.variants.find(v => v.id === item.variantId) || currentVariant;
              } else if (item.name && item.name !== liveData.title) {
                currentVariant = liveData.variants.find(v => v.name === item.name) || currentVariant;
              }

              let isAvailable = true; 
              if (currentVariant && currentVariant.availability && item.size) {
                isAvailable = currentVariant.availability[item.size]; 
              }

              return {
                ...item, 
                price: currentVariant ? currentVariant.price : item.price,
                isOutOfStock: isAvailable === false
              };
            } else {
              // If the item has no variants array (Accessories)
              return {
                ...item,
                price: liveData.price ?? item.price,
                isOutOfStock: liveData.inStock === false
              };
            }
          }
          return item; 
        } catch (error) {
          console.error("Failed to fetch fresh data for item:", item.id, error);
          return item;
        }
      })
    );
    return freshItems;
  };

  const mergeLocalCartToFirestore = async (userId) => {
    const localCart = getLocalCart();
    if (localCart.length === 0) return;
    const cartCollectionRef = collection(db, 'users', userId, 'cart');
    const firestoreCartSnapshot = await getDocs(cartCollectionRef);
    const firestoreItems = firestoreCartSnapshot.docs.map(doc => doc.data());
    const batch = writeBatch(db);

    localCart.forEach((localItem) => {
      const existingItem = firestoreItems.find(dbItem => dbItem.id === localItem.id);
      const itemRef = doc(db, 'users', userId, 'cart', localItem.id);
      if (existingItem) {
        batch.update(itemRef, { quantity: existingItem.quantity + localItem.quantity });
      } else {
        batch.set(itemRef, localItem);
      }
    });
    await batch.commit();
    localStorage.removeItem('shoppingCart');
  };

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      
      if (user) {
        const userDocRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userDocRef);
        
        if (!userDoc.exists() || !userDoc.data().email) {
          setIsLoginOpen(true); 
        }

        if (cartListenerRef.current) cartListenerRef.current();
        mergeLocalCartToFirestore(user.uid).then(() => {
          const cartCollectionRef = collection(db, 'users', user.uid, 'cart');
          cartListenerRef.current = onSnapshot(cartCollectionRef, async (snapshot) => {
            const firestoreCart = snapshot.docs.map(doc => doc.data());
            const freshCart = await fetchFreshCartData(firestoreCart);
            setCartItems(freshCart);
          });
        });
      } else {
        if (cartListenerRef.current) cartListenerRef.current();
        cartListenerRef.current = null;
        const localCart = getLocalCart();
        fetchFreshCartData(localCart).then(freshCart => setCartItems(freshCart));
      }
    });
    return () => unsubscribeAuth();
  }, [auth]);

  useEffect(() => {
    if (!currentUser) {
      localStorage.setItem('shoppingCart', JSON.stringify(cartItems));
    }
  }, [cartItems, currentUser]);

  const setupRecaptcha = (containerId) => {
    if (window.recaptchaVerifier) {
      window.recaptchaVerifier.clear();
    }
    window.recaptchaVerifier = new RecaptchaVerifier(auth, containerId, {
      'size': 'invisible'
    });
  };

  const sendOtp = async (phoneNumber, containerId) => {
    setAuthError(null);
    try {
      setupRecaptcha(containerId);
      const appVerifier = window.recaptchaVerifier;
      const confirmation = await signInWithPhoneNumber(auth, phoneNumber, appVerifier);
      setConfirmationResult(confirmation);
      return true;
    } catch (error) {
      setAuthError(error.message);
      return false;
    }
  };

  const verifyOtp = async (otpCode) => {
    setAuthError(null);
    try {
      if (!confirmationResult) throw new Error("Verification session expired.");
      const result = await confirmationResult.confirm(otpCode);
      
      const userDoc = await getDoc(doc(db, 'users', result.user.uid));
      return { 
        user: result.user, 
        isNewUser: !userDoc.exists() || !userDoc.data().email 
      };
    } catch (error) {
      setAuthError("Invalid OTP code. Please try again.");
      return null;
    }
  };

  const completeUserProfile = async (name, email) => {
    setAuthError(null);
    try {
      const user = auth.currentUser;
      if (!user) return false;

      await updateProfile(user, { displayName: name });

      await setDoc(doc(db, 'users', user.uid), {
        uid: user.uid,
        displayName: name,
        email: email,
        phoneNumber: user.phoneNumber,
        createdAt: new Date()
      }, { merge: true });

      return true;
    } catch (error) {
      setAuthError(error.message);
      return false;
    }
  };

  // --- UPDATED: Reads the incoming selected quantity instead of forcing + 1 ---
  const addToCart = async (product) => {
    const incomingQty = product.quantity || 1;

    if (currentUser) {
      const cartDocRef = doc(db, 'users', currentUser.uid, 'cart', product.id);
      const existingItem = cartItems.find(item => item.id === product.id);
      if (existingItem) {
        await updateDoc(cartDocRef, { quantity: existingItem.quantity + incomingQty });
      } else {
        await setDoc(cartDocRef, { ...product, quantity: incomingQty });
      }
    } else {
      setCartItems((prevItems) => {
        const existingItem = prevItems.find((item) => item.id === product.id);
        if (existingItem) {
          return prevItems.map((item) => 
            item.id === product.id ? { ...item, quantity: item.quantity + incomingQty } : item
          );
        }
        return [...prevItems, { ...product, quantity: incomingQty }];
      });
    }
  };

  const removeFromCart = async (id) => {
    if (currentUser) {
      await deleteDoc(doc(db, 'users', currentUser.uid, 'cart', id));
    } else {
      setCartItems((prevItems) => prevItems.filter((item) => item.id !== id));
    }
  };

  const updateQuantity = async (id, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(id);
      return;
    }
    if (currentUser) {
      await updateDoc(doc(db, 'users', currentUser.uid, 'cart', id), { quantity: newQuantity });
    } else {
      setCartItems((prevItems) => prevItems.map((item) => item.id === id ? { ...item, quantity: newQuantity } : item));
    }
  };
  
  const logout = async () => {
    setAuthError(null);
    try {
      await signOut(auth);
    } catch (error) {
      setAuthError(error.message);
    }
  };

  const value = {
    cartItems,
    addToCart,
    removeFromCart,
    updateQuantity,
    itemCount: cartItems.reduce((total, item) => total + item.quantity, 0),
    isCartOpen,
    openCart: () => setIsCartOpen(true),
    closeCart: () => setIsCartOpen(false),
    isLoginOpen,
    openLogin: () => setIsLoginOpen(true),
    closeLogin: () => setIsLoginOpen(false),
    isSearchOpen,
    openSearch: () => setIsSearchOpen(true),
    closeSearch: () => setIsSearchOpen(false),
    isAddressModalOpen,
    openAddressModal: () => setIsAddressModalOpen(true),
    closeAddressModal: () => setIsAddressModalOpen(false),
    isSizeChartOpen,
    openSizeChart,
    closeSizeChart,
    currentUser,
    authError,
    authMessage,
    clearAuthFeedback: () => setAuthError(null),
    sendOtp,
    verifyOtp,
    completeUserProfile,
    logout
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};